import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { onboardingHints } from '../../../constants';
import type { StoreType } from '../../../reducers';
import { getConversation, getCurrentConversationId } from '../../../reducers/aiAssistant.selectors';
import { selectUser } from '../../../reducers/user.selectors';
import useIsGeneratingReply from '../../../utils/useIsGeneratingReply';
import useIsMukibudeRoute from '../../../utils/useIsMukibudeRoute';
import useIsNewSession from '../../../utils/useIsNewSession';
import useIsPodcastGenerating from '../../../utils/useIsPodcastGenerating';
import useScreenSize from '../../../utils/useScreenSize';
import { useTriggeredHint } from '../../../utils/useTriggeredHint';
import Dialog from '../../Dialog/Dialog';

const AIOnlyFeedbackDialog = () => {
	const [isOpen, setIsOpen] = useState(false);
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();
	const user = useSelector(selectUser);
	const { hasSeen: hasDismissedFeedback, record: recordFeedbackDismissed } = useTriggeredHint(
		onboardingHints.aiOnlyFeedback.notShowAgain
	);
	const [lastConversationId, setLastConversationId] = useState<string | null>(null);
	const currentConversationId = useSelector(getCurrentConversationId);
	const lastConversation = useSelector((state: StoreType) =>
		getConversation(state, lastConversationId)
	);
	const isSecondOrLaterSession = useIsNewSession(onboardingHints.aiAssistant.didFirstSessionHappen);
	const isPodcastGenerating = useIsPodcastGenerating();
	const isMukibudeRoute = useIsMukibudeRoute();
	const isGeneratingReply = useIsGeneratingReply();
	// Key off the conversation, not the stream, which stays empty for podcasts.
	const hasReceivedAiResponse = (lastConversation?.data?.messages ?? []).some(
		({ role }) => role === 'assistant'
	);

	useEffect(() => {
		if (currentConversationId) {
			setLastConversationId(currentConversationId);
		}
	}, [currentConversationId]);

	useEffect(() => {
		const shouldShow =
			user?.isAiOnlyUser &&
			isSecondOrLaterSession &&
			hasReceivedAiResponse &&
			isMukibudeRoute &&
			!isGeneratingReply &&
			!isPodcastGenerating &&
			!hasDismissedFeedback &&
			!sessionStorage.getItem(onboardingHints.aiOnlyFeedback.sessionDismissed);

		if (shouldShow) {
			setIsOpen(true);
		}
	}, [
		user?.isAiOnlyUser,
		isSecondOrLaterSession,
		hasReceivedAiResponse,
		isMukibudeRoute,
		isGeneratingReply,
		isPodcastGenerating,
		hasDismissedFeedback,
	]);

	const handlePrimaryCTAClick = () => {
		const url = user?.id ? prepareFeedbackUrl(user.id) : null;

		if (url) {
			window.open(url, '_blank', 'noopener,noreferrer');
		}

		setIsOpen(false);
		recordFeedbackDismissed();
	};

	const handleSecondaryCTAClick = () => {
		setIsOpen(false);
		recordFeedbackDismissed();
	};

	const handleClose = () => {
		setIsOpen(false);
		sessionStorage.setItem(onboardingHints.aiOnlyFeedback.sessionDismissed, 'true');
	};

	const htmlBody = (
		<Box sx={{ paddingTop: 0 }}>
			<Typography variant="body1">
				{t(
					'We would like to know how Muki feels for you.' +
						' Answer three short questions so we can make Muki even better.'
				)}
			</Typography>
		</Box>
	);

	const position: 'right-bottom' | 'center' = isXsScreen ? 'center' : 'right-bottom';

	return (
		<Dialog
			open={isOpen && isMukibudeRoute}
			title={t("How's it going so far?")}
			onClose={handleClose}
			primaryCta={{
				label: t('Give feedback'),
				onClick: handlePrimaryCTAClick,
			}}
			secondaryCta={{
				label: t('Do not show more'),
				onClick: handleSecondaryCTAClick,
			}}
			htmlBody={htmlBody}
			position={position}
			dataTestId="ai-only-feedback-dialog"
		/>
	);
};

export default AIOnlyFeedbackDialog;

const prepareFeedbackUrl = (userId: string) =>
	`https://meinunterricht.typeform.com/to/g0OeNt8K#user_id=${userId}`;
